import { useState, useMemo } from "react";
import { useGetList } from "ra-core";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { usePipelines } from "../deals/usePipelines";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardKpiCards } from "./DashboardKpiCards";
import { DealsByPipelineCard } from "./DealsByPipelineCard";
import { TeamPerformanceCard } from "./TeamPerformanceCard";
import { LeadSourceAndTemperatureCard } from "./LeadSourceAndTemperatureCard";
import { LostDealsAndExtraServicesCard } from "./LostDealsAndExtraServicesCard";
import { GoalsConversionCard } from "./GoalsConversionCard";
import { TasksPerformanceCard } from "./TasksPerformanceCard";
import { PipelineHealthCard } from "./PipelineHealthCard";
import { ResponseTimeCard } from "./ResponseTimeCard";
import { filterDeals, type FilterOptions } from "./dashboardUtils";
import type { Deal, Sale, Goal, Task, DealNote } from "../types";

export const Dashboard = () => {
  const { currency, dealPipelineStatuses } = useConfigurationContext();
  const { pipelines } = usePipelines();

  const [filters, setFilters] = useState<FilterOptions>({
    period: "all",
    pipelineId: "all",
    salesId: "all",
  });

  // 1. Busca todos os negócios (deals)
  const {
    data: dealsData,
    refetch: refetchDeals,
    isPending: isPendingDeals,
    isRefetching: isRefetchingDeals,
  } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "created_at", order: "DESC" },
  });

  // 2. Busca a equipe comercial (sales)
  const {
    data: salesData,
    refetch: refetchSales,
    isPending: isPendingSales,
  } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  // 3. Busca metas cadastradas (goals)
  const {
    data: goalsData,
    refetch: refetchGoals,
    isPending: isPendingGoals,
  } = useGetList<Goal>("goals", {
    pagination: { page: 1, perPage: 100 },
  });

  // 4. Busca tarefas (tasks)
  const {
    data: tasksData,
    refetch: refetchTasks,
    isPending: isPendingTasks,
  } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 2000 },
  });

  // 5. Busca anotações de negócios (deal_notes) para cômputo de 1ª atividade
  const { data: dealNotesData, refetch: refetchDealNotes } =
    useGetList<DealNote>("deal_notes", {
      pagination: { page: 1, perPage: 2000 },
    });

  const isInitialPending =
    isPendingDeals || isPendingSales || isPendingGoals || isPendingTasks;

  const allDeals = useMemo(() => dealsData || [], [dealsData]);
  const salesTeam = useMemo(() => salesData || [], [salesData]);
  const allGoals = useMemo(() => goalsData || [], [goalsData]);
  const allTasks = useMemo(() => tasksData || [], [tasksData]);
  const allDealNotes = useMemo(() => dealNotesData || [], [dealNotesData]);

  // Aplica filtros globais (período, funil, vendedor) aos negócios
  const filteredDeals = useMemo(() => {
    return filterDeals(allDeals, filters);
  }, [allDeals, filters]);

  // Filtra tarefas conforme vendedor selecionado
  const filteredTasks = useMemo(() => {
    if (filters.salesId === "all") return allTasks;
    return allTasks.filter(
      (t) => String(t.sales_id) === String(filters.salesId),
    );
  }, [allTasks, filters.salesId]);

  // Filtra metas conforme funil ou vendedor selecionado
  const filteredGoals = useMemo(() => {
    return allGoals.filter((g) => {
      if (
        filters.pipelineId !== "all" &&
        g.pipeline_id &&
        String(g.pipeline_id) !== String(filters.pipelineId)
      ) {
        return false;
      }
      if (
        filters.salesId !== "all" &&
        g.sales_id &&
        String(g.sales_id) !== String(filters.salesId)
      ) {
        return false;
      }
      return true;
    });
  }, [allGoals, filters]);

  const wonStages = useMemo(() => {
    const defaultWon = ["won", "venda"];
    if (dealPipelineStatuses && dealPipelineStatuses.length > 0) {
      return Array.from(new Set([...defaultWon, ...dealPipelineStatuses]));
    }
    return defaultWon;
  }, [dealPipelineStatuses]);

  const lostStages = useMemo(() => ["lost", "perdido"], []);

  const handleRefreshAll = () => {
    refetchDeals();
    refetchSales();
    refetchGoals();
    refetchTasks();
    refetchDealNotes();
  };

  const isRefreshing = isRefetchingDeals;

  if (isInitialPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3 text-muted-foreground">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-xs font-medium">
          Carregando indicadores do dashboard...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 pt-2">
      {/* Cabeçalho e Filtros Globais */}
      <DashboardHeader
        filters={filters}
        onFilterChange={setFilters}
        pipelines={pipelines}
        salesTeam={salesTeam}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshing}
      />

      {/* 1. Indicador Principal: Funil de Vendas (Cards Superiores: Todos, Em Aberto, Ganhos, Perdidos) */}
      <DashboardKpiCards
        deals={filteredDeals}
        wonStages={wonStages}
        lostStages={lostStages}
        currency={currency}
      />

      {/* 2. Painel Response Time (Tempo de Resposta do 1º Contato) */}
      <ResponseTimeCard
        deals={allDeals}
        tasks={allTasks}
        dealNotes={allDealNotes}
        salesTeam={salesTeam}
        filters={filters}
      />

      {/* 3. Saúde do Funil (Passagem por cada etapa do funil sem duplicidade e negócios ganhos) */}
      <PipelineHealthCard
        deals={allDeals}
        pipelines={pipelines}
        salesTeam={salesTeam}
        filters={filters}
        wonStages={wonStages}
        lostStages={lostStages}
        currency={currency}
        defaultPipelineId={filters.pipelineId}
      />

      {/* 4. Metas × Meses & Conversão × Metas */}
      <GoalsConversionCard
        goals={filteredGoals}
        deals={filteredDeals}
        salesTeam={salesTeam}
        currency={currency}
      />

      {/* 5. Ganhos por Funil & Em Aberto por Funil */}
      <DealsByPipelineCard
        deals={filteredDeals}
        pipelines={pipelines}
        wonStages={wonStages}
        lostStages={lostStages}
        currency={currency}
      />

      {/* 6. Desempenho da Equipe (leads por vendedor) */}
      <TeamPerformanceCard
        deals={filteredDeals}
        salesTeam={salesTeam}
        wonStages={wonStages}
        lostStages={lostStages}
        currency={currency}
      />

      {/* 7. Por Origem & Leads por Temperatura */}
      <LeadSourceAndTemperatureCard deals={filteredDeals} currency={currency} />

      {/* 8. Perdidos por Motivo de Perda & Serviço Extra Vendido */}
      <LostDealsAndExtraServicesCard
        deals={filteredDeals}
        lostStages={lostStages}
        wonStages={wonStages}
        currency={currency}
      />

      {/* 9. Desempenho de Tarefas por Status */}
      <TasksPerformanceCard tasks={filteredTasks} />
    </div>
  );
};
